import React, { useState, useEffect, MouseEventHandler } from 'react';
import { AxiosResponse } from 'axios';
import xw from 'xwind';
import { observer } from 'mobx-react-lite';
import { loggedActionsGuard } from '@/utils/loggedActionsGuard';

import FridgeDoorStore from '@/stores/FridgeDoorStore';
import { CareIcon, HeartIcon, ThumbSubIcon } from '../../../../components';
import { AuthStore } from '../../../../stores';
import { ReactionTypes, ReactionValueTypes } from '../../constants';
import { StyledReactinButton, Text } from './Reactions.styles';

interface ReactionButtonProps {
  type: ReactionValueTypes;
  reactions: any;
  addReaction: (type: ReactionValueTypes) => Promise<AxiosResponse<any>>;
  removeReaction: (type: ReactionValueTypes) => Promise<AxiosResponse<any>>;
}

const icons = {
  [ReactionTypes.HEART]: HeartIcon,
  [ReactionTypes.THUMBSUB]: ThumbSubIcon,
  [ReactionTypes.SMILE]: CareIcon,
};

const initialValue = {
  count: 0,
  is_reacted: false,
};

function ReactionButton({
  type,
  reactions,
  addReaction,
  removeReaction,
  ...props
}: ReactionButtonProps): JSX.Element {
  const [inProcessing, setInProcessing] = useState(false);
  const [reaction, setReaction] = useState(reactions?.[type] ?? initialValue);
  const { isMustCreateVillage } = FridgeDoorStore;

  const Icon = icons[type];

  useEffect(() => {
    const updatedReaction = reactions?.[type] ?? initialValue;
    setReaction(updatedReaction);
  }, [reactions, type]);

  function increment() {
    setReaction((prevReaction) => ({
      count: (prevReaction?.count ?? 0) + 1,
      is_reacted: true,
    }));
  }

  function decrement() {
    setReaction((prevReaction) => ({
      count: (prevReaction?.count ?? 1) - 1,
      is_reacted: false,
    }));
  }

  async function handleAddReaction() {
    try {
      increment();
      await addReaction(type);
    } catch (error) {
      decrement();
    } finally {
      setInProcessing(false);
    }
  }

  async function handleRemoveReaction() {
    try {
      decrement();
      await removeReaction(type);
    } catch (error) {
      increment();
    } finally {
      setInProcessing(false);
    }
  }

  const handleClick = (loggedActionsGuard(
    AuthStore.isLoggedIn,
    isMustCreateVillage,
    () => {
      setInProcessing(true);

      if (reaction.is_reacted) {
        handleRemoveReaction();
      } else {
        handleAddReaction();
      }
    },
  ) as unknown) as MouseEventHandler;

  return (
    <StyledReactinButton
      {...props}
      onClick={handleClick}
      css={[
        reaction.is_reacted && xw`bg-primary text-white`,
        inProcessing && xw`animate-pulse cursor-wait`,
      ]}
      disabled={inProcessing}
    >
      <Icon />
      <Text>{reaction?.count || 0}</Text>
    </StyledReactinButton>
  );
}

export default observer(ReactionButton);
